/**
 * Single source of truth for care-admin access:
 * which permission unlocks which app, sidebar item, and routes.
 *
 * Wildcards (admin.* / platform.*) are applied in permissions.js, not listed here.
 */

export const APPLICATIONS = [
  { key: 'care-admin', label: 'Care Admin', hint: 'This HMO operations console' },
  { key: 'facility', label: 'Facility app', hint: 'Provider / facility portal' },
  { key: 'enrollee', label: 'Enrollee / HR portal', hint: 'Member and employer portals' },
  { key: 'shared', label: 'Shared / API', hint: 'Used across apps or API-only' },
];

export const ROLE_USER_TYPES = [
  { key: 'staff', label: 'Staff (Care Admin)' },
  { key: 'admin', label: 'Admin (Care Admin)' },
  { key: 'facility', label: 'Facility' },
  { key: 'client', label: 'Enrollee' },
  { key: 'corporation', label: 'Employer / HR' },
];

export const HOME_MODULE_ORDER = ['claims', 'preauth', 'dashboard'];

/**
 * Alias → canonical. hasPermission treats each pair as one capability
 * (same map as API RBACService). Wildcards are not aliases.
 */
export const PERMISSION_ALIASES = {
  'preauth.approve': 'preauth.decide',
  'pa.flag': 'preauth.flag',
};

/** Canonical slug for a permission (identity if it is not an alias). */
export function canonicalPermission(name) {
  if (!name) return name;
  return PERMISSION_ALIASES[name] || name;
}

/** Canonical slug plus every alias that maps to it. */
export function permissionAliasFamily(name) {
  const canonical = canonicalPermission(name);
  const family = [canonical];
  Object.entries(PERMISSION_ALIASES).forEach(([alias, canon]) => {
    if (canon === canonical && !family.includes(alias)) family.push(alias);
  });
  return family;
}

/** A wildcard slug (`admin.*`, `email_campaigns.*`) rather than a single permission. */
export function isWildcardPermission(name) {
  return String(name ?? '').includes('*');
}

/**
 * Permissions the assigner toggle grants. Defaults to anyOf.
 *
 * `anyOf` answers "who may OPEN this screen" and therefore mixes read and write
 * permissions — a screen is reachable by viewers and managers alike. Handing
 * that whole list out turned "give this role the Organizations screen" into
 * "give this role corporations.manage", and "give this role Facilities" into
 * "give this role facilities.manage", which is how facility login details
 * became reachable by anyone who could see the facility list.
 *
 * So `assigns` is the grant list, and it should name only what a *view* of the
 * screen needs. Write capability is granted deliberately, permission by
 * permission. A module whose whole purpose IS the action (a moderation queue)
 * may name its manage permission here — explicitly, so it reads as a decision.
 *
 * Wildcards are stripped, and that is a security boundary rather than tidying.
 * `anyOf` answers "who may open this screen", so it legitimately lists
 * `admin.*` as one of the holders — Check-in codes did exactly that. Reusing
 * that list to decide what to GRANT turned "give this role check-in codes"
 * into "give this role every admin permission", which is how a Client Success
 * Manager ended up with an Admin account. A wildcard is a role-level decision:
 * build an Admin role deliberately, never as a side effect of a module toggle.
 *
 * NOTE: as of the catalog-driven role editor this has no production caller —
 * the screen switches it backed are gone, and a role is now built by ticking
 * catalog actions one at a time (see rolePermissionGrouping). It is kept
 * because the `assigns`-over-`anyOf` and wildcard-stripping rules above are
 * real invariants worth holding under test, and because any future feature
 * that derives permissions from a screen must obey them.
 */
export function grantPermissionsForModule(mod) {
  if (!mod) return [];
  const source = Array.isArray(mod.assigns) && mod.assigns.length
    ? mod.assigns
    : (mod.anyOf || []);
  return source.filter((name) => !isWildcardPermission(name));
}

/** Care Admin screens that can be a role landing page. */
export function careAdminHomeSelectOptions() {
  const groups = {};
  getAccessModules()
    .filter((mod) => (
      mod.application === 'care-admin'
      && mod.routes?.[0]
      && mod.allow_home !== false
      && mod.is_active !== false
    ))
    .forEach((mod) => {
      if (!groups[mod.group]) groups[mod.group] = [];
      groups[mod.group].push({
        value: mod.key,
        label: `${mod.label} (${mod.routes[0]})`,
      });
    });
  return Object.entries(groups).map(([label, options]) => ({ label, options }));
}

/** @typedef {{ key: string, label: string, group: string, application: string, anyOf: string[], assigns?: string[], routes: string[], description: string }} AccessModule */

/** @type {AccessModule[]} */
export const ACCESS_MODULES = [
  {
    key: 'dashboard',
    label: 'Dashboard (Overview)',
    group: 'Dashboard',
    application: 'care-admin',
    anyOf: ['users.read_own', 'dashboard.view'],
    routes: ['/dashboard', '/dashboard/enrollees'],
    description: 'Open the home overview and enrollees dashboard.',
  },
  {
    key: 'search',
    label: 'Search',
    group: 'Dashboard',
    application: 'care-admin',
    anyOf: ['users.read_own'],
    routes: ['/search'],
    description: 'Use global search across enrollees, encounters, and claims.',
  },
  {
    key: 'clientVerification',
    label: 'Client Verification',
    group: 'Care',
    application: 'care-admin',
    anyOf: ['users.read', 'users.read_all', 'clients.read_all'],
    routes: ['/verify'],
    description: 'Verify and update enrollee records.',
  },
  {
    key: 'encounters',
    label: 'Encounters',
    group: 'Care',
    application: 'care-admin',
    anyOf: ['checkins.read', 'checkins.manage', 'encounters.read', 'encounters.manage'],
    assigns: ['checkins.read', 'encounters.read'],
    routes: ['/checkins'],
    description: 'Browse facility check-ins / encounters.',
  },
  {
    key: 'appointments',
    label: 'Appointments',
    group: 'Care',
    application: 'care-admin',
    anyOf: ['appointments.read_all', 'appointments.manage_all'],
    assigns: ['appointments.read_all'],
    routes: ['/admin/appointments'],
    description: 'View and manage appointments across facilities.',
  },
  {
    key: 'encounterCodes',
    label: 'Check-in codes',
    group: 'Care',
    application: 'care-admin',
    // admin.* is here because admins can reach the screen — it is NOT something
    // this toggle should hand out. assigns is what the toggle grants.
    anyOf: ['visit_codes.manage_all', 'admin.*', 'checkins.create'],
    assigns: ['visit_codes.manage_all', 'checkins.create'],
    routes: ['/search'],
    description: 'View, generate, and manage enrollee facility check-in codes from search results.',
  },
  {
    key: 'onboardingOtp',
    label: 'OTP Lookup',
    group: 'Care',
    application: 'care-admin',
    anyOf: ['onboarding.otp_reveal'],
    routes: ['/admin/onboarding-otp'],
    description: 'Look up pending one-time codes of every kind (registration, enrollee account sign-up and password reset, policy-account password reset) during an SMS or email provider outage.',
  },
  {
    key: 'announcements',
    label: 'Announcements',
    group: 'System',
    application: 'care-admin',
    anyOf: ['announcements.manage'],
    assigns: ['announcements.manage'],
    routes: ['/admin/announcements'],
    description: 'Create, schedule, target and archive the home-screen announcement banner shown in the member mobile app.',
  },
  {
    key: 'facilityNotices',
    label: 'Facility notices',
    group: 'System',
    application: 'care-admin',
    anyOf: ['facility_notices.manage'],
    assigns: ['facility_notices.manage'],
    routes: ['/admin/facility-notices'],
    description: 'Create, target, schedule and deactivate notices shown at the top of the Facility Portal.',
  },
  {
    key: 'formSettings',
    label: 'Form settings',
    group: 'Dashboard',
    application: 'care-admin',
    anyOf: ['form_config.manage'],
    assigns: ['form_config.manage'],
    routes: ['/admin/form-settings'],
    description: 'Reference lists (states, gender, relationship, titles) and registration form settings: which fields show, which are required, their labels and order.',
  },
  {
    key: 'preauth',
    label: 'Pre-Authorization',
    group: 'Care',
    application: 'care-admin',
    anyOf: ['preauth.read', 'preauth.decide', 'preauth.approve'],
    assigns: ['preauth.read'],
    routes: ['/preauth', '/preauth-action', '/file-encounter'],
    description: 'Open the PA queue and review encounters. Toggle grants view (preauth.read) only — Accept/Reject is Pre-Authorization decisions.',
  },
  {
    key: 'preauthApproval',
    label: 'Pre-Authorization decisions',
    group: 'Care',
    application: 'care-admin',
    anyOf: ['preauth.decide', 'preauth.approve'],
    routes: ['/preauth-action'],
    allow_home: false,
    nav_visible: false,
    description: 'Accept or reject PA line items on the encounter page. Holding preauth.decide or preauth.approve is enough (tick them below or use this switch). preauth.approve is an alias of preauth.decide.',
  },
  {
    key: 'preauthAnalytics',
    label: 'PA Dashboard',
    group: 'Dashboard',
    application: 'care-admin',
    anyOf: ['preauth.analytics', 'preauth.read', 'claims.reports'],
    routes: ['/dashboard/preauth'],
    description: 'PA analytics and encounter drill-down on the dashboard.',
  },
  {
    key: 'claims',
    label: 'Claims Processing',
    group: 'Claims',
    application: 'care-admin',
    anyOf: ['claims.read', 'claims.read_all'],
    routes: ['/claims', '/claims-action', '/claims/filings'],
    description: 'Open claims queues and filing screens.',
  },
  {
    key: 'claimsApproval',
    label: 'Claims approval',
    group: 'Claims',
    application: 'care-admin',
    anyOf: ['claims.approve'],
    routes: ['/claims'],
    description: 'Approve claims (used with claims.read to work the queue).',
  },
  {
    key: 'claimsPayment',
    label: 'Claims payment',
    group: 'Claims',
    application: 'care-admin',
    anyOf: ['claims.payment'],
    routes: ['/claims'],
    description: 'Payment workflow and payment-advice exports.',
  },
  {
    key: 'staffManagement',
    label: 'Staff Management',
    group: 'System',
    application: 'care-admin',
    anyOf: ['users.manage_roles'],
    routes: ['/dash'],
    description: 'Create staff accounts and attach roles.',
  },
  {
    key: 'organizations',
    label: 'Organizations',
    group: 'Administration',
    application: 'care-admin',
    anyOf: ['corporations.read', 'corporations.read_all', 'corporations.manage', 'organizations.read'],
    assigns: ['corporations.read', 'corporations.read_all', 'organizations.read'],
    routes: ['/corporations', '/corporations/staff-uploads', '/corporations/enrollment-holds'],
    description: 'Employer / scheme records, staff uploads, enrollment holds.',
  },
  {
    key: 'planRequests',
    label: 'Plan requests',
    group: 'Administration',
    application: 'care-admin',
    anyOf: ['corporations.manage', 'organizations.manage'],
    routes: ['/corporations/plan-requests'],
    description: 'Approve HR plan add/remove requests.',
  },
  {
    key: 'bulkUploadLogs',
    label: 'Bulk upload logs',
    group: 'Administration',
    application: 'care-admin',
    anyOf: ['corporations.view_reports', 'corporations.read_own'],
    routes: ['/corporations'],
    description: 'Read bulk-enrolment upload audit on an organisation.',
  },
  {
    key: 'orgCarePrograms',
    label: 'Org care programs',
    group: 'Administration',
    application: 'care-admin',
    anyOf: ['care_programs.manage_org', 'plans.manage'],
    routes: ['/corporations'],
    description: 'Enable care programs on an organisation.',
  },
  {
    key: 'careProgramsDesigner',
    label: 'Care program designer',
    group: 'Administration',
    application: 'care-admin',
    anyOf: ['care_programs.manage', 'plans.manage'],
    routes: ['/plans/care-programs/:programId'],
    description: 'Create and publish care programs.',
  },
  {
    key: 'careProgramUtilization',
    label: 'Care program utilization',
    group: 'Administration',
    application: 'care-admin',
    anyOf: ['care_programs.read_all_utilization', 'corporations.view_reports', 'enrollees.read_all'],
    routes: ['/plans/care-programs/utilization'],
    description: 'Cross-org care program utilization report.',
  },
  {
    key: 'plans',
    label: 'Plans',
    group: 'Administration',
    application: 'care-admin',
    anyOf: ['plans.read', 'plans.manage', 'tariffs.read', 'tariffs.read_all', 'tariffs.manage'],
    assigns: ['plans.read', 'tariffs.read', 'tariffs.read_all'],
    routes: ['/plans', '/plans/:plan'],
    description: 'Plan catalog, plan detail, encounter types.',
  },
  {
    key: 'tariffs',
    label: 'Tariffs',
    group: 'Administration',
    application: 'care-admin',
    anyOf: ['tariffs.read', 'tariffs.read_all', 'tariffs.manage', 'tariffs.standard.manage'],
    assigns: ['tariffs.read', 'tariffs.read_all'],
    routes: ['/admin/tariffs', '/facilities/:id/tariffs'],
    description: 'Standard and facility tariff screens.',
  },
  {
    key: 'facilities',
    label: 'Facilities',
    group: 'Administration',
    application: 'care-admin',
    anyOf: ['facilities.read_all', 'facilities.read_list', 'facilities.manage'],
    assigns: ['facilities.read_all', 'facilities.read_list'],
    routes: ['/facilities', '/facilities/:facilityId'],
    description: 'Facility directory and facility detail.',
  },
  {
    key: 'facilitySuggestions',
    label: 'Facility suggestions',
    group: 'Administration',
    application: 'care-admin',
    anyOf: ['facilities.read_all'],
    routes: ['/facilities/suggestions'],
    description: 'Enrollee facility-suggestion queue.',
  },
  {
    key: 'facilitiesListing',
    label: 'Facility listing',
    group: 'Administration',
    application: 'care-admin',
    anyOf: ['facilities.read_list', 'facilities.read_all'],
    routes: ['/facilities'],
    description: 'Read-only facility directory.',
  },
  {
    key: 'facilitiesExport',
    label: 'Facility export',
    group: 'Administration',
    application: 'care-admin',
    anyOf: ['facilities.export'],
    routes: ['/facilities'],
    description: 'Export the facility directory as CSV.',
  },
  {
    key: 'integrationsSaaSPro',
    label: 'Integration partners',
    group: 'System',
    application: 'care-admin',
    anyOf: ['admin.integrations_manage'],
    routes: ['/admin/integrations/partners', '/admin/integrations/saaspro'],
    description: 'SaaSPro / PA-agent partner configuration and activity.',
  },
  {
    key: 'broadcastNotifications',
    label: 'Communications',
    group: 'System',
    application: 'care-admin',
    anyOf: ['notifications.broadcast', 'email_campaigns.*'],
    assigns: ['notifications.broadcast'],
    routes: ['/admin/campaigns'],
    description: 'Communications: push notifications, email campaigns and announcements.',
  },
  {
    key: 'rbacAdmin',
    label: 'RBAC Admin',
    group: 'System',
    application: 'care-admin',
    anyOf: ['admin.role_management'],
    routes: ['/rbac'],
    description: 'Create roles, assign permissions, and inspect the glossary.',
  },
  {
    key: 'platformContexts',
    label: 'Platform contexts',
    group: 'System',
    application: 'care-admin',
    anyOf: ['platform.*', 'platform.operator'],
    routes: ['/platform/contexts', '/platform/identity-generation-settings'],
    description: 'CLI-only operator tools (act-as contexts, identity generation).',
  },
  {
    key: 'activityLog',
    label: 'Activity Log',
    group: 'System',
    application: 'care-admin',
    anyOf: ['logs.read', 'admin.audit_logs'],
    routes: ['/log'],
    description: 'System activity / audit log.',
  },
  {
    key: 'opsEmailRecipients',
    label: 'Ops email recipients',
    group: 'System',
    application: 'care-admin',
    anyOf: ['admin.system_config'],
    routes: ['/admin/settings/notifications'],
    description: 'Operational email recipient lists.',
  },
  {
    key: 'notificationSystem',
    label: 'Notification system',
    group: 'System',
    application: 'care-admin',
    anyOf: ['admin.*', 'admin.system_config'],
    routes: ['/admin/settings/notification-system'],
    description: 'Notification retention and system failure incidents (Resolved / Not Resolved).',
  },
  {
    key: 'smsProviderSettings',
    label: 'SMS provider',
    group: 'System',
    application: 'care-admin',
    anyOf: ['admin.system_config'],
    routes: ['/admin/settings/sms-provider'],
    description: 'Switch the active outgoing SMS provider.',
  },
  {
    // Not a sidebar item: reached from the Claims module strip (ModuleNav).
    // Same gate as GET|PUT /v2/admin/claims-rollout.
    key: 'claimsRollout',
    label: 'Claims rollout switches',
    group: 'System',
    application: 'care-admin',
    anyOf: ['admin.system_config'],
    routes: ['/admin/settings/claims-rollout'],
    description: 'Turn parts of the new encounter/claims flow on for pilot facilities.',
  },
  {
    // Same gate as GET /v2/admin/test-subjects. Toggling the module grants read
    // only; marking needs test_subjects.manage (or admin.*), granted in RBAC and
    // checked strictly by the API.
    key: 'testSubjects',
    label: 'Test subjects',
    group: 'System',
    application: 'care-admin',
    anyOf: ['test_subjects.read', 'test_subjects.manage', 'admin.system_config'],
    assigns: ['test_subjects.read'],
    routes: ['/admin/settings/test-subjects'],
    description: 'Mark organisations, policies, enrollees, facilities and staff as test: they keep working, but nothing they touch counts in any figure, report or payment.',
  },
  {
    key: 'helpContent',
    label: 'Facility Portal help',
    group: 'Administration',
    application: 'care-admin',
    anyOf: ['help_content.manage'],
    assigns: ['help_content.manage'],
    routes: ['/admin/help-content'],
    description: 'How To guides for Facility Portal pages and the tutorial video library.',
  },
  {
    key: 'facilityRatings',
    label: 'Facility ratings',
    group: 'Administration',
    application: 'care-admin',
    anyOf: ['admin.system_config', 'facility_ratings.manage_all'],
    assigns: ['facility_ratings.manage_all'],
    routes: ['/admin/facility-ratings'],
    description: 'Moderate individual facility feedback/ratings submitted by members.',
  },
  {
    key: 'facilityTypes',
    label: 'Facility types',
    group: 'Administration',
    application: 'care-admin',
    anyOf: ['admin.system_config'],
    routes: ['/admin/settings/facility-types'],
    description: 'Create, rename, and delete facility type categories (Hospital, Clinic, Pharmacy, etc).',
  },
  {
    key: 'planManagement',
    label: 'Plan Management (new model)',
    group: 'System',
    application: 'care-admin',
    anyOf: ['plan_designer.manage'],
    assigns: ['plan_designer.manage'],
    routes: ['/admin/plan-management'],
    description: 'The new plan model workspace: coverage rows, pools and windows (the designer), bands & pricing, the per-plan cutover switchboard, and event-sourced analytics. Dark-launch surface running in parallel with the legacy plan screens.',
  },
  {
    key: 'notificationsCenter',
    label: 'Notifications',
    group: 'Dashboard',
    application: 'care-admin',
    anyOf: ['users.read_own'],
    routes: ['/notifications', '/admin/notification-preferences'],
    description: 'In-app notification centre and preferences.',
  },
  {
    key: 'checkinCleanup',
    label: 'Soft-deleted check-ins',
    group: 'Care',
    application: 'care-admin',
    anyOf: ['checkins.read', 'encounters.manage'],
    assigns: ['checkins.read'],
    routes: ['/admin/soft-deleted-checkins'],
    description: 'Review and restore soft-deleted check-ins.',
  },
];

export const MODULE_PERMISSIONS = Object.fromEntries(
  ACCESS_MODULES.map((mod) => [mod.key, mod.anyOf])
);

/**
 * Legacy staff role ids per module — the ONE table for "who used to reach this screen".
 *
 * Only consulted when the RBAC permission map is empty (see permissions.canStayOnModule /
 * hasAnyPermissionWithLegacy). Never let these ids override a populated map.
 *
 * Transcribed from the pre-RBAC ProtectedRoute.legacyStaffCanAccess table, which is what
 * actually gated routing; Sidebar.hasAccess and the (now deleted) features/nav.js held
 * divergent copies. Role ids: 1 Super User, 2 Customer Care, 3 Claims Officer,
 * 4 Head Of Medicals, 5 Head of Finance, 6 Aman Admin, 7 IT Admin, 8 Marketing, 9 Director.
 *
 * Default for anything not listed: [1] — the old table short-circuited `roleId === 1` for
 * every module and denied every other role.
 */
export const ALL_LEGACY_STAFF_ROLE_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const LEGACY_CLAIMS_ROLE_IDS = [1, 3, 4, 5];
const LEGACY_PREAUTH_ROLE_IDS = [1, 2, 3, 4];
const LEGACY_SUPER_ONLY = [1];

export const LEGACY_MODULE_ROLE_IDS = {
  // Open to any legacy staff account
  dashboard: ALL_LEGACY_STAFF_ROLE_IDS,
  search: ALL_LEGACY_STAFF_ROLE_IDS,
  clientVerification: ALL_LEGACY_STAFF_ROLE_IDS,
  encounters: ALL_LEGACY_STAFF_ROLE_IDS,

  // Claims workflow
  claims: LEGACY_CLAIMS_ROLE_IDS,
  claimsApproval: LEGACY_CLAIMS_ROLE_IDS,
  claimsPayment: LEGACY_CLAIMS_ROLE_IDS,
  // NOTE: Sidebar.hasAccess also grants pendingClaims to [1,3,4,5]; ProtectedRoute did not,
  // so a non-super legacy account saw the nav item and was bounced off the route.
  // ProtectedRoute is canonical — keep [1] until the divergence is resolved deliberately.

  // Pre-authorisation
  preauth: LEGACY_PREAUTH_ROLE_IDS,
  preauthApproval: LEGACY_PREAUTH_ROLE_IDS,
  preauthAnalytics: LEGACY_PREAUTH_ROLE_IDS,

  // Super-user-only surfaces
  staffManagement: LEGACY_SUPER_ONLY,
  rbacAdmin: LEGACY_SUPER_ONLY,
  activityLog: LEGACY_SUPER_ONLY,

  // Everything else the old table reached only via the `roleId === 1` short-circuit
  appointments: LEGACY_SUPER_ONLY,
  encounterCodes: LEGACY_SUPER_ONLY,
  onboardingOtp: LEGACY_SUPER_ONLY,
  organizations: LEGACY_SUPER_ONLY,
  planRequests: LEGACY_SUPER_ONLY,
  bulkUploadLogs: LEGACY_SUPER_ONLY,
  orgCarePrograms: LEGACY_SUPER_ONLY,
  careProgramsDesigner: LEGACY_SUPER_ONLY,
  careProgramUtilization: LEGACY_SUPER_ONLY,
  plans: LEGACY_SUPER_ONLY,
  tariffs: LEGACY_SUPER_ONLY,
  facilities: LEGACY_SUPER_ONLY,
  facilitySuggestions: LEGACY_SUPER_ONLY,
  facilitiesListing: LEGACY_SUPER_ONLY,
  facilitiesExport: LEGACY_SUPER_ONLY,
  integrationsSaaSPro: LEGACY_SUPER_ONLY,
  broadcastNotifications: LEGACY_SUPER_ONLY,
  opsEmailRecipients: LEGACY_SUPER_ONLY,
  notificationSystem: LEGACY_SUPER_ONLY,
  smsProviderSettings: LEGACY_SUPER_ONLY,
  claimsRollout: LEGACY_SUPER_ONLY,
  testSubjects: LEGACY_SUPER_ONLY,
  facilityNotices: LEGACY_SUPER_ONLY,
  facilityRatings: LEGACY_SUPER_ONLY,
  helpContent: LEGACY_SUPER_ONLY,
  facilityTypes: LEGACY_SUPER_ONLY,
  planManagement: LEGACY_SUPER_ONLY,
  // Every person has their own notification centre (Notification System v2 §3.1); the
  // API scopes it to the caller, so no role can see anyone else's notifications.
  notificationsCenter: ALL_LEGACY_STAFF_ROLE_IDS,
  checkinCleanup: LEGACY_SUPER_ONLY,

  // platform.* / platform.operator is CLI-managed and must never be reachable by a
  // legacy staff role id, however empty the permission map is.
  platformContexts: [],
};

/** @type {AccessModule[]|null} */
let runtimeAccessModules = null;

/**
 * How the in-memory catalog was last populated.
 *
 * - `live`        — the API answered with at least one row. Built-in data used for a key
 *                   here is a MISCONFIGURATION: the row is not reaching this app.
 * - `empty`       — the API answered with zero rows (nothing seeded / everything filtered out).
 * - `unavailable` — no fetch has succeeded yet (no token, request threw). Built-ins are the
 *                   legitimate fallback in this state; nothing is wrong.
 */
export const ACCESS_CATALOG_STATES = {
  LIVE: 'live',
  EMPTY: 'empty',
  UNAVAILABLE: 'unavailable',
};

const CATALOG_STATE_VALUES = Object.values(ACCESS_CATALOG_STATES);

let accessCatalogState = ACCESS_CATALOG_STATES.UNAVAILABLE;
/** Module keys the API actually returned on the last successful fetch. */
let liveModuleKeys = new Set();
/** Built-in keys standing in for a live row while the catalog is `live`. */
const builtInFallbackKeys = new Set();
/** Keys already warned about — one console warning per key per session. */
const warnedFallbackKeys = new Set();

function resetFallbackTracking() {
  builtInFallbackKeys.clear();
  warnedFallbackKeys.clear();
}

/** @returns {'live'|'empty'|'unavailable'} */
export function getAccessCatalogState() {
  return accessCatalogState;
}

/**
 * Record how the catalog was last populated. Leaving `live` drops the recorded
 * mismatches: built-ins are expected when there is no live catalog to disagree with.
 */
export function setAccessCatalogState(state) {
  const next = CATALOG_STATE_VALUES.includes(state)
    ? state
    : ACCESS_CATALOG_STATES.UNAVAILABLE;
  if (next === accessCatalogState) return accessCatalogState;
  accessCatalogState = next;
  if (next !== ACCESS_CATALOG_STATES.LIVE) resetFallbackTracking();
  return accessCatalogState;
}

/** Module keys present in the last successful fetch. */
export function getLiveModuleKeys() {
  return Array.from(liveModuleKeys);
}

/** True when this key came from the API on the last successful fetch. */
export function isModuleKeyLive(moduleKey) {
  return Boolean(moduleKey) && liveModuleKeys.has(moduleKey);
}

/**
 * Built-in module keys that are standing in for an Access Modules row while the
 * catalog is live — i.e. edits to those rows cannot reach the app.
 */
export function getFallbackModuleKeys() {
  return Array.from(builtInFallbackKeys);
}

/** True when built-in data is silently substituting for a live row. */
function isBuiltInFallback(moduleKey) {
  return Boolean(moduleKey)
    && accessCatalogState === ACCESS_CATALOG_STATES.LIVE
    && !liveModuleKeys.has(moduleKey)
    && ACCESS_MODULES.some((m) => m.key === moduleKey);
}

/** Record (silently) that this key is running on built-in data. */
function recordBuiltInFallback(moduleKey) {
  if (!isBuiltInFallback(moduleKey)) return;
  builtInFallbackKeys.add(moduleKey);
}

/** Record + warn once per key, at the point the built-in mapping is actually consumed. */
function reportBuiltInFallback(moduleKey) {
  if (!isBuiltInFallback(moduleKey)) return;
  builtInFallbackKeys.add(moduleKey);
  if (warnedFallbackKeys.has(moduleKey)) return;
  warnedFallbackKeys.add(moduleKey);
  // eslint-disable-next-line no-console
  console.warn(
    `[accessCatalog] Module "${moduleKey}" is running on Care Admin's built-in permission `
    + 'mapping: its Access Modules row is not reaching this app. Most likely its "application" '
    + 'is not "care-admin", or the row is inactive (or was never registered). Edits to that row '
    + 'in RBAC Admin → Access Modules have no effect until it is set to application "care-admin" '
    + 'and Active.'
  );
}

function truthyFlag(value, fallback = true) {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'boolean') return value;
  return ['1', 'true', 'yes', 'on'].includes(String(value).toLowerCase());
}

/** Coerce catalog any_of / routes / assigns from API arrays or JSON strings. */
function stringList(value) {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[')) {
      try {
        const decoded = JSON.parse(trimmed);
        if (Array.isArray(decoded)) return decoded.map(String).filter(Boolean);
      } catch {
        // fall through to comma split
      }
    }
    return trimmed.split(',').map((part) => part.trim()).filter(Boolean);
  }
  return [];
}

/** Normalize API / static catalog rows to the shape used by care-admin. */
export function normalizeAccessModule(row = {}) {
  const key = String(row.key || row.module_key || '');
  const builtIn = key ? ACCESS_MODULES.find((m) => m.key === key) : null;
  const anyOfList = stringList(row.anyOf || row.any_of);
  const resolvedAnyOf = anyOfList.length ? anyOfList : [...(builtIn?.anyOf || [])];
  const routesList = stringList(row.routes);
  const resolvedRoutes = routesList.length ? routesList : [...(builtIn?.routes || [])];
  const assignsRaw = row.assigns || row.grant || builtIn?.assigns;
  const assignsList = stringList(assignsRaw);
  // Same boundary as grantPermissionsForModule: an operator-edited catalog row
  // whose any_of mentions a wildcard must not turn that into a granted wildcard.
  const assigns = (assignsList.length ? assignsList : [...resolvedAnyOf])
    .filter((name) => !isWildcardPermission(name));
  return {
    id: row.id,
    key,
    label: String(row.label || builtIn?.label || ''),
    group: String(row.group || row.module_group || builtIn?.group || 'Other'),
    application: String(row.application || builtIn?.application || 'care-admin'),
    anyOf: resolvedAnyOf,
    assigns,
    routes: resolvedRoutes,
    description: String(row.description || builtIn?.description || ''),
    allow_home: truthyFlag(row.allow_home ?? row.allowHome ?? builtIn?.allow_home, true),
    nav_visible: truthyFlag(row.nav_visible ?? row.navVisible ?? builtIn?.nav_visible, true),
    is_system: truthyFlag(row.is_system ?? row.isSystem, Boolean(builtIn?.is_system)),
    is_active: truthyFlag(row.is_active ?? row.isActive, builtIn?.is_active !== false),
    sort_order: Number(row.sort_order ?? row.sortOrder ?? builtIn?.sort_order ?? 0),
  };
}

/**
 * Replace the in-memory catalog with API rows. Empty / invalid lists keep the built-in fallback.
 * Built-in modules missing from the API (stale DB seed) stay available so canAccess
 * can still resolve keys like preauthApproval from their anyOf.
 *
 * Access outcomes are unchanged by the bookkeeping here: the returned list is built
 * exactly as before, and only the catalog-state / mismatch records are new.
 *
 * @param {unknown} list
 * @param {'live'|'empty'|'unavailable'} [state] how the list was obtained; defaults to
 *   `live` for a non-empty list, `empty` for an empty array, `unavailable` otherwise.
 */
export function setRuntimeAccessModules(list, state) {
  if (!Array.isArray(list) || list.length === 0) {
    runtimeAccessModules = null;
    liveModuleKeys = new Set();
    setAccessCatalogState(state || (Array.isArray(list)
      ? ACCESS_CATALOG_STATES.EMPTY
      : ACCESS_CATALOG_STATES.UNAVAILABLE));
    resetFallbackTracking();
    return getAccessModules();
  }
  const byKey = new Map();
  list.map(normalizeAccessModule).filter((mod) => mod.key).forEach((mod) => {
    byKey.set(mod.key, mod);
  });
  const ordered = [];
  const seen = new Set();
  ACCESS_MODULES.forEach((builtIn) => {
    ordered.push(byKey.get(builtIn.key) || normalizeAccessModule(builtIn));
    seen.add(builtIn.key);
  });
  byKey.forEach((mod, key) => {
    if (!seen.has(key)) ordered.push(mod);
  });
  runtimeAccessModules = ordered;
  liveModuleKeys = new Set(byKey.keys());
  setAccessCatalogState(state || ACCESS_CATALOG_STATES.LIVE);
  resetFallbackTracking();
  // Every built-in key the API did not return is a built-in substitution. Record it
  // now (silently) so the Access Modules tab can show the full picture; the console
  // warning is emitted lazily, when the built-in mapping is actually read.
  ACCESS_MODULES.forEach((builtIn) => {
    if (!byKey.has(builtIn.key)) recordBuiltInFallback(builtIn.key);
  });
  return runtimeAccessModules;
}

export function getAccessModules() {
  return runtimeAccessModules || ACCESS_MODULES;
}

export function getModulePermissions() {
  return Object.fromEntries(getAccessModules().map((mod) => [mod.key, mod.anyOf]));
}

/**
 * @type {Record<string, { displayName: string, application: string, resource: string, description: string }>}
 */
export const PERMISSION_GLOSSARY = {
  'users.read_own': {
    displayName: 'View own profile',
    application: 'shared',
    resource: 'users',
    description: 'Sign in and see your own profile. Also unlocks Dashboard, Search, and Notifications in Care Admin.',
  },
  'users.update_own': {
    displayName: 'Update own profile',
    application: 'shared',
    resource: 'users',
    description: 'Edit your own name, email, and phone.',
  },
  'users.read': {
    displayName: 'View users',
    application: 'care-admin',
    resource: 'users',
    description: 'Read staff / enrollee records used by Client Verification.',
  },
  'users.read_all': {
    displayName: 'View all users',
    application: 'care-admin',
    resource: 'users',
    description: 'Directory of all users; used by Client Verification. Does not unlock Staff Management.',
  },
  'users.create': {
    displayName: 'Create users',
    application: 'care-admin',
    resource: 'users',
    description: 'Create staff accounts (Staff Management).',
  },
  'users.update': {
    displayName: 'Update users',
    application: 'care-admin',
    resource: 'users',
    description: 'Edit other users’ accounts.',
  },
  'users.delete': {
    displayName: 'Delete users',
    application: 'care-admin',
    resource: 'users',
    description: 'Remove user accounts.',
  },
  'users.manage_roles': {
    displayName: 'Manage user roles',
    application: 'care-admin',
    resource: 'users',
    description: 'Attach and remove roles on staff. Unlocks Staff Management.',
  },
  'dashboard.view': {
    displayName: 'View dashboard',
    application: 'care-admin',
    resource: 'dashboard',
    description: 'Unified analytics dashboards.',
  },
  'preauth.read': {
    displayName: 'View pre-authorizations',
    application: 'care-admin',
    resource: 'preauth',
    description: 'Open the PA queue, encounter page, and PA dashboard.',
  },
  'preauth.create': {
    displayName: 'Create pre-authorizations',
    // claims_manager, medical_officer and customer_service all hold this: filing a PA is an admin job too, not facility-only.
    application: 'shared',
    resource: 'preauth',
    description: 'File PA items from the facility app.',
  },
  'preauth.update': {
    displayName: 'Update pre-authorizations',
    application: 'shared',
    resource: 'preauth',
    description: 'Edit an existing PA encounter.',
  },
  'preauth.decide': {
    displayName: 'Decide pre-authorizations',
    application: 'care-admin',
    resource: 'preauth',
    description: 'Accept or reject PA lines on the encounter page.',
  },
  'preauth.approve': {
    displayName: 'Approve pre-authorizations',
    application: 'care-admin',
    resource: 'preauth',
    description: 'Alias of preauth.decide — same Accept/Reject capability.',
  },
  'preauth.manual': {
    displayName: 'Manual PA actions',
    application: 'care-admin',
    resource: 'preauth',
    description: 'Admit enrollee / add extra PA items from the encounter page.',
  },
  'preauth.analytics': {
    displayName: 'PA analytics',
    application: 'care-admin',
    resource: 'preauth',
    description: 'PA Dashboard charts and encounter lists.',
  },
  'preauth.flag': {
    displayName: 'Flag PA items',
    application: 'care-admin',
    resource: 'preauth',
    description: 'Raise flags on PA lines.',
  },
  'pa.flag': {
    displayName: 'Flag PA items (alias)',
    application: 'care-admin',
    resource: 'pa',
    description: 'Alias of preauth.flag.',
  },
  'claims.read': {
    displayName: 'View claims',
    application: 'care-admin',
    resource: 'claims',
    description: 'Open Claims Processing and related filing screens.',
  },
  'claims.read_all': {
    displayName: 'View all claims',
    application: 'care-admin',
    resource: 'claims',
    description: 'Same queue as claims.read; some roles are granted this name instead.',
  },
  'claims.create': {
    displayName: 'Create claims',
    // claims_manager, claims_officer and it_admin hold this: admins file claims too.
    application: 'shared',
    resource: 'claims',
    description: 'Submit claims from the facility app.',
  },
  'claims.update': {
    displayName: 'Update claims',
    application: 'shared',
    resource: 'claims',
    description: 'Edit claim content.',
  },
  'claims.approve': {
    displayName: 'Approve claims',
    application: 'care-admin',
    resource: 'claims',
    description: 'Approve a claim in the processing queue.',
  },
  'claims.reject': {
    displayName: 'Reject claims',
    application: 'care-admin',
    resource: 'claims',
    description: 'Reject a claim with a reason.',
  },
  'claims.review': {
    displayName: 'Review claims',
    application: 'care-admin',
    resource: 'claims',
    description: 'Medical review step in the claims workflow.',
  },
  'claims.process': {
    displayName: 'Process claims',
    application: 'care-admin',
    resource: 'claims',
    description: 'Move claims through the workflow.',
  },
  'claims.reports': {
    displayName: 'Claims reports',
    application: 'care-admin',
    resource: 'claims',
    description: 'Claims reporting; also unlocks PA Dashboard as a fallback.',
  },
  'claims.payment': {
    displayName: 'Claims payment',
    application: 'care-admin',
    resource: 'claims',
    description: 'Payment workflow and payment-advice exports.',
  },
  'checkins.read': {
    displayName: 'View check-ins',
    application: 'shared',
    resource: 'checkins',
    description: 'List encounters in Care Admin and the facility app.',
  },
  'visit_codes.manage_all': {
    displayName: 'Manage check-in codes',
    application: 'care-admin',
    resource: 'visit_codes',
    description: 'View, generate, and cancel enrollee check-in codes from Care Admin.',
  },
  'checkins.create': {
    displayName: 'Create check-ins',
    application: 'facility',
    resource: 'checkins',
    description: 'Start an encounter in the facility app.',
  },
  'checkins.update': {
    displayName: 'Update check-ins',
    application: 'facility',
    resource: 'checkins',
    description: 'Edit an open encounter in the facility app.',
  },
  'checkins.close': {
    displayName: 'Close check-ins',
    application: 'facility',
    resource: 'checkins',
    description: 'Close an encounter in the facility app. Does not cover admin checkout — see encounters.checkout.',
  },
  'encounters.checkout': {
    displayName: 'Check out encounters',
    application: 'care-admin',
    resource: 'encounters',
    description: 'Close an open encounter and file its claim. Creates a payable, so it is deliberately separate from editing or discharging.',
  },
  'encounters.read': {
    displayName: 'View encounters',
    application: 'care-admin',
    resource: 'encounters',
    description: 'Same Encounters list as checkins.read.',
  },
  'encounters.manage': {
    displayName: 'Manage encounters',
    application: 'care-admin',
    resource: 'encounters',
    description: 'Manage encounters and soft-deleted check-ins.',
  },
  'appointments.read_all': {
    displayName: 'View all appointments',
    application: 'care-admin',
    resource: 'appointments',
    description: 'Appointments screen in Care Admin.',
  },
  'appointments.manage_all': {
    displayName: 'Manage all appointments',
    application: 'care-admin',
    resource: 'appointments',
    description: 'Create / update appointments across facilities (same nav as read_all).',
  },
  'appointments.read_facility': {
    displayName: 'View facility appointments',
    application: 'facility',
    resource: 'appointments',
    description: 'Appointments for the signed-in facility.',
  },
  'appointments.checkin_facility': {
    displayName: 'Check in appointments',
    application: 'facility',
    resource: 'appointments',
    description: 'Convert a facility appointment into a check-in.',
  },
  'onboarding.otp_reveal': {
    displayName: 'OTP lookup',
    application: 'care-admin',
    resource: 'onboarding',
    description: 'Show pending one-time codes (registration, enrollee account, password reset) so support can read them out during a provider outage.',
  },
  'clients.read_all': {
    displayName: 'View all enrollees',
    application: 'care-admin',
    resource: 'clients',
    description: 'Client Verification and enrollee search.',
  },
  'clients.read': {
    displayName: 'View enrollees',
    application: 'shared',
    resource: 'clients',
    description: 'Read enrollee records in Care Admin or facility search.',
  },
  'clients.read_own': {
    displayName: 'View own enrolment',
    application: 'enrollee',
    resource: 'clients',
    description: 'Member portal: own biodata.',
  },
  'clients.verify': {
    displayName: 'Verify enrollees',
    application: 'care-admin',
    resource: 'clients',
    description: 'Mark enrollee records verified.',
  },
  'corporations.read': {
    displayName: 'View organisations',
    application: 'care-admin',
    resource: 'corporations',
    description: 'Organizations list in Care Admin.',
  },
  'corporations.read_all': {
    displayName: 'View all organisations',
    application: 'care-admin',
    resource: 'corporations',
    description: 'Same Organizations nav as corporations.read.',
  },
  'corporations.manage': {
    displayName: 'Manage organisations',
    application: 'care-admin',
    resource: 'corporations',
    description: 'Edit organisations and approve plan requests.',
  },
  'corporations.manage_policies': {
    displayName: 'Manage organization plans',
    application: 'care-admin',
    resource: 'corporations',
    description: 'Assign and remove the plans an organization offers (Manage plans). Separate from approving plan requests, and from viewing the organization.',
  },
  'corporations.read_own': {
    displayName: 'View own organisation',
    application: 'enrollee',
    resource: 'corporations',
    description: 'HR portal: own employer record.',
  },
  'corporations.view_reports': {
    displayName: 'Organisation reports',
    application: 'care-admin',
    resource: 'corporations',
    description: 'Org reports, bulk-upload logs, and program utilization.',
  },
  'organizations.read': {
    displayName: 'View organisations (alias)',
    application: 'care-admin',
    resource: 'organizations',
    description: 'Alias used by some routes for the Organizations list.',
  },
  'organizations.manage': {
    displayName: 'Manage organisations (alias)',
    application: 'care-admin',
    resource: 'organizations',
    description: 'Alias for plan-request approval.',
  },
  'organizations.reports': {
    displayName: 'Organisation reports (alias)',
    application: 'care-admin',
    resource: 'organizations',
    description: 'Organisation reporting APIs.',
  },
  'facilities.read_all': {
    displayName: 'View all facilities',
    application: 'care-admin',
    resource: 'facilities',
    description: 'Facilities list, detail, and suggestions.',
  },
  'facilities.read_list': {
    displayName: 'View facility listing',
    application: 'care-admin',
    resource: 'facilities',
    description: 'Read-only facility directory.',
  },
  'facilities.manage': {
    displayName: 'Manage facilities',
    application: 'care-admin',
    resource: 'facilities',
    description: 'Create and update facilities.',
  },
  'facilities.send_login_details': {
    displayName: 'Send facility login details',
    application: 'care-admin',
    resource: 'facilities',
    description: 'Provision and email a facility portal credential. Deliberately separate from managing a facility: handing out a login is a credential action, not an edit.',
  },
  'facilities.set_user_password': {
    displayName: 'Set facility user password',
    application: 'care-admin',
    resource: 'facilities',
    description: 'Set a facility portal user\'s password directly. Separate from managing facility staff, because it hands over account access rather than changing a record.',
  },
  'facilities.export': {
    displayName: 'Export facilities',
    application: 'care-admin',
    resource: 'facilities',
    description: 'CSV export of the facility directory.',
  },
  'facilities.read_own': {
    displayName: 'View own facility',
    application: 'facility',
    resource: 'facilities',
    description: 'Facility app: own facility profile.',
  },
  'facilities.update_own': {
    displayName: 'Update own facility',
    application: 'facility',
    resource: 'facilities',
    description: 'Facility app: edit own facility profile.',
  },
  'plans.read': {
    displayName: 'View plans',
    application: 'care-admin',
    resource: 'plans',
    description: 'Plans list and plan detail.',
  },
  'plans.manage': {
    displayName: 'Manage plans',
    application: 'care-admin',
    resource: 'plans',
    description: 'Edit plans, care programs, and costing.',
  },
  'tariffs.read': {
    displayName: 'View tariffs',
    application: 'care-admin',
    resource: 'tariffs',
    description: 'Standard / facility tariff screens.',
  },
  'tariffs.read_all': {
    displayName: 'View all tariffs',
    application: 'care-admin',
    resource: 'tariffs',
    description: 'Same tariff nav as tariffs.read.',
  },
  'tariffs.manage': {
    displayName: 'Manage tariffs',
    application: 'care-admin',
    resource: 'tariffs',
    description: 'Edit facility tariffs.',
  },
  'tariffs.standard.manage': {
    displayName: 'Manage standard tariff',
    application: 'care-admin',
    resource: 'tariffs',
    description: 'Edit the HMO standard tariff.',
  },
  'tariffs.read_own': {
    displayName: 'View own tariffs',
    application: 'facility',
    resource: 'tariffs',
    description: 'Facility app: own tariff list.',
  },
  'pricing.read': {
    displayName: 'View pricing',
    application: 'care-admin',
    resource: 'pricing',
    description: 'Costing tables.',
  },
  'pricing.update': {
    displayName: 'Update pricing',
    application: 'care-admin',
    resource: 'pricing',
    description: 'Edit costing / costable rows.',
  },
  'care_programs.manage': {
    displayName: 'Manage care programs',
    application: 'care-admin',
    resource: 'care_programs',
    description: 'Care program designer.',
  },
  'care_programs.manage_org': {
    displayName: 'Manage org care programs',
    application: 'care-admin',
    resource: 'care_programs',
    description: 'Enable programs on an organisation.',
  },
  'care_programs.read_all_utilization': {
    displayName: 'View all program utilization',
    application: 'care-admin',
    resource: 'care_programs',
    description: 'Cross-org utilization report.',
  },
  'enrollees.read_all': {
    displayName: 'View all enrollees (programs)',
    application: 'care-admin',
    resource: 'enrollees',
    description: 'Enrollee lists used by program utilization.',
  },
  'admin.role_management': {
    displayName: 'Role management',
    application: 'care-admin',
    resource: 'admin',
    description: 'RBAC Admin only — create roles, assign permissions. No longer grants Integration partners or Soft-deleted check-ins; those have their own permissions now.',
  },
  'admin.integrations_manage': {
    displayName: 'Manage integration partners',
    application: 'care-admin',
    resource: 'admin',
    description: 'Integration partners screen (SaaSPro / PA-agent configuration) without granting RBAC Admin.',
  },
  'admin.*': {
    displayName: 'All admin permissions',
    application: 'care-admin',
    resource: 'admin',
    description: 'Wildcard: every Care Admin screen except Platform contexts.',
  },
  'admin.system_config': {
    displayName: 'System configuration',
    application: 'care-admin',
    resource: 'admin',
    description: 'Ops email recipients and similar system settings.',
  },
  'admin.audit_logs': {
    displayName: 'Audit logs',
    application: 'care-admin',
    resource: 'admin',
    description: 'Activity Log (together with logs.read).',
  },
  'logs.read': {
    displayName: 'Read activity logs',
    application: 'care-admin',
    resource: 'logs',
    description: 'Activity Log.',
  },
  'notifications.broadcast': {
    displayName: 'Broadcast notifications',
    application: 'care-admin',
    resource: 'notifications',
    description: 'Communications (push / in-app broadcast).',
  },
  'email_campaigns.*': {
    displayName: 'Email campaigns (all)',
    application: 'care-admin',
    resource: 'email_campaigns',
    description: 'Wildcard: same Campaigns screen as notifications.broadcast.',
  },
  'platform.*': {
    displayName: 'Platform operator (all)',
    application: 'care-admin',
    resource: 'platform',
    description: 'CLI-managed break-glass. Unlocks Platform contexts and every other permission.',
  },
  'platform.operator': {
    displayName: 'Platform operator',
    application: 'care-admin',
    resource: 'platform',
    description: 'Alias of platform.*. Not assignable in this UI.',
  },
};

export function applicationLabel(key) {
  return APPLICATIONS.find((a) => a.key === key)?.label || key || 'Shared / API';
}

export function getAccessModule(key) {
  if (!key) return null;
  return getAccessModules().find((m) => m.key === key)
    || ACCESS_MODULES.find((m) => m.key === key)
    || null;
}

/**
 * anyOf for a module key, including built-in fallback when the live catalog is stale or empty.
 *
 * The returned value is unchanged by the reporting below — it only notes that a built-in
 * mapping was served while a live catalog was in hand, which means an Access Modules row
 * for this key is not reaching the app.
 */
export function getModuleAnyOf(moduleKey) {
  reportBuiltInFallback(moduleKey);
  const live = getAccessModule(moduleKey)?.anyOf;
  if (Array.isArray(live) && live.length) return live;
  const builtIn = ACCESS_MODULES.find((m) => m.key === moduleKey)?.anyOf;
  return Array.isArray(builtIn) ? builtIn : [];
}

export function modulesForPermission(permissionName) {
  if (!permissionName) return [];
  return getAccessModules().filter((mod) => (mod.anyOf || []).includes(permissionName));
}

/**
 * Permissions only the facility (provider) portal can use.
 *
 * Derived from evidence, not vibes: each is held ONLY by facility_* roles in
 * RBACSeeder, is referenced by no Care Admin access module, and appears
 * nowhere in care-admin's own source. Several near-misses were excluded for
 * failing exactly one of those — checkins.close, checkins.update,
 * encounters.discharge and facilities.manage_staff are all held only by
 * facility roles but ARE used by Care Admin screens, so they stay shared.
 *
 * The `_own` permissions are the clearest case: "own" means the facility you
 * belong to, which a staff account does not have, so granting one to an admin
 * role does nothing at all.
 */
const FACILITY_ONLY_PERMISSIONS = new Set([
  'appointments.checkin_facility',
  'appointments.read_facility',
  'clients.read',
  'encounters.create',
  'encounters.update',
  'facilities.manage_pricing',
  'facilities.read_own',
  'facilities.update_own',
  'pa.flag',
  'tariffs.adopt_standard',
  'tariffs.create_own',
  'tariffs.delete_own',
  'tariffs.propose_own',
  'tariffs.read_own',
  'tariffs.submit_own',
  'tariffs.update_own',
]);

/**
 * Permissions only the enrollee / employer portals can use. Same derivation.
 *
 * corporations.manage_policies is deliberately NOT here despite being held
 * only by corporation_admin: care-admin's Manage plans gate accepts it, so
 * hiding it would break assigning it to the admin roles that need it.
 */
const ENROLLEE_ONLY_PERMISSIONS = new Set([
  'appointments.cancel_own',
  'appointments.read_own',
  'appointments.update_own',
  'claims.read_own',
  'clients.manage_dependents',
  'clients.read_dependents',
  'clients.read_own',
  'clients.update_own',
  'corporations.bulk_operations',
  'corporations.manage_employees',
  'corporations.read_own',
  'corporations.update_own',
  'enrollees.create_own',
  'enrollees.read_own',
  'enrollees.update_own',
  'facility_ratings.create_own',
  'facility_ratings.read_own',
  'payments.read_own',
  'visit_codes.cancel_own',
  'visit_codes.create_own',
  'visit_codes.read_own',
]);

/**
 * Which portal a permission belongs to, by exact slug.
 *
 * Resource alone cannot answer this — `tariffs` carries both the admin review
 * queue and the facility's own filings, and `facilities` carries both the
 * admin directory and a provider's view of itself. Inferring from the prefix
 * is what put facility permissions in front of admin roles.
 */
export function applicationForPermission(name) {
  const slug = String(name || '');
  if (FACILITY_ONLY_PERMISSIONS.has(slug)) return 'facility';
  if (ENROLLEE_ONLY_PERMISSIONS.has(slug)) return 'enrollee';
  return null;
}

function inferApplication(resource) {
  if ([
    'preauth', 'claims', 'plans', 'tariffs', 'admin', 'logs', 'notifications',
    'email_campaigns', 'pricing', 'organizations', 'corporations', 'onboarding',
    'care_programs', 'enrollees', 'platform', 'dashboard',
  ].includes(resource)) {
    return 'care-admin';
  }
  if (resource === 'clients' || resource === 'users' || resource === 'facilities'
    || resource === 'checkins' || resource === 'appointments' || resource === 'encounters') {
    return 'shared';
  }
  return 'shared';
}

export function formatAffects(modules) {
  if (!modules.length) return 'No Care Admin screen mapped — API / other app only.';
  return modules
    .map((mod) => `${mod.label} (${(mod.routes || []).join(', ')})`)
    .join(' · ');
}

export function permissionMeta(name, fallback = {}) {
  const known = PERMISSION_GLOSSARY[name] || {};
  const resource = known.resource || fallback.resource || String(name || '').split('.')[0] || 'general';
  // Slug first: a per-permission answer beats a per-resource guess, and the
  // glossary's own tag beats both only when the slug map has nothing to say.
  const application = applicationForPermission(name)
    || known.application
    || inferApplication(resource);
  return {
    name,
    displayName: known.displayName || fallback.display_name || fallback.displayName || name,
    description: known.description || fallback.description || '',
    application,
    resource,
    affects: formatAffects(modulesForPermission(name)),
  };
}

export function groupByApplication(items, getAppKey) {
  const buckets = APPLICATIONS.map((app) => ({
    ...app,
    items: [],
  }));
  const extra = { key: 'other', label: 'Other', hint: '', items: [] };
  items.forEach((item) => {
    const key = getAppKey(item) || 'shared';
    const bucket = buckets.find((b) => b.key === key);
    if (bucket) bucket.items.push(item);
    else extra.items.push(item);
  });
  const filled = buckets.filter((b) => b.items.length);
  return extra.items.length ? [...filled, extra] : filled;
}
